import React, { useState, useEffect, useRef } from 'react';
import { Sun, Thermometer, Briefcase, Zap, Snowflake, Layers, Cloud, Wind, CloudLightning, SunSnow } from 'lucide-react';

const GustIcon = ({ size = 24, strokeWidth = 2, style }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        <path d="M3 8h10a2 2 0 1 0-2-2" />
        <path d="M3 12h15a2 2 0 1 1-2 2" />
        <path d="M3 16h7a2 2 0 1 0-2 2" />
    </svg>
);

const LayerPanel = ({ viewMode, setViewMode, isPanelOpen, inline }) => {
    const [isHovered, setIsHovered] = useState(false);
    const panelRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                setIsHovered(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, []);

    const layers = [
        { id: 'client', icon: Briefcase, label: 'Client View', color: '#76AB3F' },
        { id: 'score', icon: Sun, label: 'Solar Health', color: '#F59E0B' },
        { id: 'irradiance', icon: Zap, label: 'Irradiance', color: '#8B5CF6' },
        { id: 'temperature', icon: Thermometer, label: 'Temperature', color: '#EF4444' },
        { id: 'wind', icon: Wind, label: 'Wind Speed', color: '#06B6D4' },
        { id: 'wind_gust', icon: GustIcon, label: 'Wind Gust', color: '#1D4ED8' },
        { id: 'cloud', icon: Cloud, label: 'Cloud Cover', color: '#6B7280' },
        { id: 'thunderstorm', icon: CloudLightning, label: 'Thunderstorms', color: '#4F46E5' },
        { id: 'snow', icon: Snowflake, label: 'Snow Cover', color: '#38BDF8' },
    ];

    if (inline) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', gap: '8px',
                marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #E5EDDB'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Layers size={12} style={{ color: '#4a7a28' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4a7a28' }}>Map Layers</span>
                </div>
                {layers.map(layer => {
                    const isActive = viewMode === layer.id;
                    const Icon = layer.icon;
                    return (
                        <button
                            key={layer.id}
                            onClick={() => setViewMode(isActive ? 'client' : layer.id)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                background: isActive ? layer.color : 'transparent',
                                color: isActive ? '#ffffff' : '#374151',
                                border: isActive ? `1px solid ${layer.color}` : '1px solid transparent',
                                borderRadius: '10px', padding: '8px 12px',
                                cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                                fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                if (!isActive) e.currentTarget.style.background = '#F4FBF0';
                            }}
                            onMouseLeave={(e) => {
                                if (!isActive) e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <Icon size={16} strokeWidth={2.5} />
                            <span>{layer.label}</span>
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="layer-panel-container"
             ref={panelRef}
             onMouseEnter={() => setIsHovered(true)}
             onMouseLeave={() => setIsHovered(false)}
             onClick={() => setIsHovered(p => !p)}
             style={{
                 position: 'absolute',
                 right: 65,
                 bottom: 75,
                 zIndex: 1000, 
                 transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                 display: 'flex',
                 flexDirection: 'column',
                 alignItems: 'flex-end',
                 gap: '3px',
             }}>
            {isHovered ? (
                layers.map(layer => {
                    const isActive = viewMode === layer.id;
                    const Icon = layer.icon;
                    return (
                        <button
                            key={layer.id}
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                setViewMode(isActive ? 'client' : layer.id);
                                if (window.matchMedia('(max-width: 768px)').matches) {
                                    setIsHovered(false);
                                }
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                background: isActive ? layer.color : '#ffffff',
                                color: isActive ? '#ffffff' : '#374151',
                                border: 'none',
                                borderRadius: '20px',
                                padding: '6px 12px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                fontFamily: 'Inter, sans-serif',
                                boxShadow: isActive 
                                    ? `0 4px 12px ${layer.color}66` 
                                    : '0 2px 8px rgba(0,0,0,0.15)',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                if (!isActive) e.currentTarget.style.background = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                                if (!isActive) e.currentTarget.style.background = '#ffffff';
                            }}
                        >
                            <Icon size={14} strokeWidth={isActive ? 3 : 2.5} />
                            <span>{layer.label}</span>
                        </button>
                    );
                })
            ) : (
                <button
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: '#ffffff', color: '#4a7a28', border: 'none',
                        borderRadius: '50%', width: '40px', height: '40px',
                        cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <SunSnow size={20} strokeWidth={2.5} />
                </button>
            )}
        </div>
    );
};

export default LayerPanel;
